// Apolo — Self-Repair CHECKER (INFRA). "How do we verify this project, and did it pass?"
// @arch:pure-engine
//
// Two pure-ish functions over INJECTED PORTS (no FS, no Electron, no React):
//   detectVerifyCommand(...) — decide the verify command (test > lint > typecheck > build)
//   runCheck(...)            — run it via a `runCommand` port, parse exitCode → passed,
//                             extract the failure EVIDENCE the FIXER sub-run reads.
//
// The EVIDENCE is the load-bearing output: the most relevant failure lines, capped so a
// 150 KB log can't blow up a prompt. runCheck NEVER throws — a thrown/rejected port or a
// missing command resolves to a result object, because a checker that crashes would break
// the whole turn. "Nothing to check" (no command) is a PASS, not a failure.
//
// Dependency Rule (Tier 1): this file depends INWARD on the port shapes only; concretions
// (the real run_command IPC, the real fs.readFile) are wired at the composition root.

/** Max evidence size (~3 KB): big enough for a stack + a few failed assertions, small
 *  enough to never dominate a prompt. Cap is on the SLICED-out failure region. */
export const EVIDENCE_CAP = 3072;

// package.json scripts we trust to "verify", in priority order. Conservative: only
// read-ish gates (test/lint/typecheck/build) — never `start`, `dev`, `deploy`, etc.
const SCRIPT_PRECEDENCE = ['test', 'lint', 'typecheck', 'build'];

// Map a chosen generic script → its runnable, NON-INTERACTIVE command. `test` gets the
// `--run` guard so a watch-mode test runner (vitest/jest default) exits instead of hanging
// the post-turn loop. The rest are plain `npm run <name>`, already one-shot.
function runnableFor(scriptName) {
  if (scriptName === 'test') return 'npm test -- --run';
  return `npm run ${scriptName}`;
}

function nonEmptyString(v) {
  return typeof v === 'string' && v.trim() !== '';
}

/**
 * Decide the verify command for `root`.
 *
 * Precedence: `configuredCmd` (verbatim) → package.json scripts (test > lint > typecheck >
 * build, returned in runnable non-interactive form) → null (nothing to verify).
 *
 * @param {object}   args
 * @param {string}   args.root            project root (passed to the port; not read here).
 * @param {string=}  args.configuredCmd   an explicit override; used verbatim if non-blank.
 * @param {(root:string)=>object|null} args.readPackageJson  INJECTED port: read + parse
 *        package.json, or return null. Never read the FS here. A thrown port = "no pkg".
 * @returns {string|null} the command to run, or null when there is nothing to verify.
 */
export function detectVerifyCommand({ root, readPackageJson, configuredCmd } = {}) {
  // 1) Explicit override wins, verbatim (trimmed).
  if (nonEmptyString(configuredCmd)) return configuredCmd.trim();

  // 2) package.json scripts, by precedence. The port is the ONLY way we touch the project.
  let pkg = null;
  if (typeof readPackageJson === 'function') {
    try {
      pkg = readPackageJson(root);
    } catch {
      pkg = null; // a broken port is "no package.json", never a throw
    }
  }

  const scripts = pkg && typeof pkg === 'object' ? pkg.scripts : null;
  if (scripts && typeof scripts === 'object') {
    for (const name of SCRIPT_PRECEDENCE) {
      if (nonEmptyString(scripts[name])) return runnableFor(name);
    }
  }

  // 3) Nothing to verify.
  return null;
}

// Pull the most relevant failure region out of a stream, then cap it. Heuristic: prefer
// the lines AROUND the first failure marker (so we keep the actual error, not the leading
// boilerplate); otherwise keep the TAIL (where summaries/stack traces land). Then hard-cap.
const FAILURE_MARKERS = [
  /\berror\b/i,
  /\bfailed\b/i,
  /\bfail\b/i,
  /✗/,
  /✕/,
  /\bassert/i,
  /\bexception\b/i,
  /\bexpected\b/i,
  /\bcannot\b/i,
  /\bundefined\b/i,
];

function firstFailureLine(lines) {
  for (let i = 0; i < lines.length; i += 1) {
    if (FAILURE_MARKERS.some((re) => re.test(lines[i]))) return i;
  }
  return -1;
}

function capTail(s, cap) {
  if (s.length <= cap) return s;
  // Keep the tail (most relevant for summaries/stacks); mark the elision.
  return `…(truncated)\n${s.slice(s.length - cap)}`.slice(-cap);
}

function extractEvidence(stderr, stdout) {
  const raw = nonEmptyString(stderr) ? stderr : nonEmptyString(stdout) ? stdout : '';
  const text = String(raw).replace(/\s+$/u, '');
  if (text === '') return '';

  const lines = text.split('\n');
  const idx = firstFailureLine(lines);

  let region;
  if (idx >= 0) {
    // A window around the first failure: a little lead-in + the meat after it.
    const start = Math.max(0, idx - 2);
    region = lines.slice(start).join('\n');
  } else {
    region = text;
  }

  return capTail(region.trim(), EVIDENCE_CAP);
}

// AMBIENTE ausente ≠ regressão de código. Um worktree sem node_modules roda
// `npm test` e o binário some ("sh: vitest: command not found", exit 127) — o
// check NÃO PÔDE rodar. Isso não é vermelho pra reparar: o driver deve PULAR
// (senão vira um needs_planning falso a cada turno de código no worktree).
// Conservador: exit 127 é inequívoco (runner de teste jamais sai 127 numa
// assertion); ENOENT = spawn falhou; o texto "command not found" só conta
// quando NÃO há exit normal de falha (1/2), pra não pular uma falha real que
// por acaso cite isso. PURO.
const ENV_MISSING_RE = /\bENOENT\b|\bcommand not found\b|:\s*not found\b|\bis not recognized\b/i;
function isNotRunnable(exitCode, evidence) {
  if (exitCode === 127) return true;
  const t = String(evidence || '');
  if (/\bENOENT\b/i.test(t)) return true;
  return exitCode == null && ENV_MISSING_RE.test(t);
}

function errToString(err) {
  if (err == null) return 'unknown error';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message || String(err);
  try {
    return typeof err.message === 'string' ? err.message : String(err);
  } catch {
    return 'unknown error';
  }
}

/**
 * Run the verify command and classify the outcome. NEVER throws / rejects.
 *
 * @param {object} args
 * @param {string} args.root  cwd for the run.
 * @param {(a:{command:string,cwd:string})=>Promise<{ok:boolean,exitCode:number|null,stdout:string,stderr:string}>} args.runCommand
 *        INJECTED port (the existing shell exec). A throw/reject is caught here.
 * @param {string|null} args.cmd  the command from detectVerifyCommand; null/blank ⇒ skip.
 * @returns {Promise<{passed:boolean, evidence:string, cmd:string|null, exitCode:number|null, notRunnable?:boolean}>}
 *   - no cmd → { passed:true, evidence:'', cmd:null, exitCode:null }  (nothing to check ≠ fail)
 *   - exitCode === 0 → { passed:true,  evidence:'',        cmd, exitCode:0 }
 *   - exitCode !== 0 → { passed:false, evidence:<failure>, cmd, exitCode, notRunnable }
 *   - port throws    → { passed:false, evidence:<error>,   cmd, exitCode:null, notRunnable:true }
 *   `notRunnable` = o check NÃO PÔDE rodar (toolchain ausente: exit 127 / ENOENT).
 *   O driver PULA o reparo nesse caso (não é regressão de código).
 */
export async function runCheck({ root, runCommand, cmd } = {}) {
  // Nothing to verify is a PASS, not a failure — the driver simply skips repair.
  if (!nonEmptyString(cmd)) {
    return { passed: true, evidence: '', cmd: null, exitCode: null };
  }
  const command = cmd.trim();

  try {
    if (typeof runCommand !== 'function') {
      throw new Error('runCheck: no runCommand port provided');
    }

    const res = (await runCommand({ command, cwd: root })) || {};
    const exitCode = typeof res.exitCode === 'number' ? res.exitCode : null;
    const passed = exitCode === 0;

    if (passed) {
      return { passed: true, evidence: '', cmd: command, exitCode };
    }

    const evidence = extractEvidence(res.stderr, res.stdout) || `verify failed (exit ${exitCode ?? 'unknown'})`;
    return { passed: false, evidence, cmd: command, exitCode, notRunnable: isNotRunnable(exitCode, evidence) };
  } catch (err) {
    // A crashing port (spawn ENOENT, etc.) means the check COULDN'T RUN — trata como
    // ambiente ausente (notRunnable), não como vermelho pra reparar.
    return { passed: false, evidence: errToString(err), cmd: command, exitCode: null, notRunnable: true };
  }
}
